import { Edit, EyeEmpty, Search, Group } from 'iconoir-react';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { LOADING } from '../../../redux_store/actionTypes';
import { getUsers } from '../../../redux_store/users/users_thunk';
import Pagination from '../../templates/Pagination';
import Tabs from '../../templates/Tabs';
import LoadingModal from '../../templates/modals/LoadingModal';
import ExportDataButton from '../../templates/ExportDataButton';

const UsersPage = () => {
  const dispatch = useDispatch();
  const navigator = useNavigate();
  const [tabSelected, setTabSelected] = useState('All');
  const [systemUsers, setSystemUsers] = useState([]);
  const { users, status } = useSelector((state) => state.user);
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 8;
  const totalPages = Math.ceil(systemUsers.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;

  useEffect(() => {
    dispatch(getUsers(tabSelected.toLowerCase()));
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    if (users && Array.isArray(users))
      setSystemUsers(users)
  }, [users])

  const handleTabSelection = (e) => {
    const tab = e.target.innerText;
    const account_type = tab !== 'All' ? tab.substring(0, tab.length - 1) : tab;
    dispatch(getUsers(account_type.toLowerCase()));
    setTabSelected(tab);
  }

  return (
    <div className='space-y-6'>
      {status === LOADING && <LoadingModal message={'Loading users...'} />}

      {/* Page Header */}
      <div className='flex items-center justify-between'>
        <div>
          <h1 className='text-2xl font-bold text-surface-800'>User Management</h1>
          <p className='text-surface-500 mt-1'>View and manage all platform users</p>
        </div>
        <ExportDataButton data={systemUsers} filename='wayride-users' />
      </div>

      {/* Filters */}
      <div className='card'>
        <div className='flex flex-col lg:flex-row lg:items-center justify-between gap-4'>
        <Tabs
          active={tabSelected}
          heading={['All', 'Drivers', 'Customers']}
          onTabSelect={handleTabSelection}
        />
          <div className='w-full lg:w-80'>
            <div className='relative'>
              <div className='absolute left-4 top-1/2 -translate-y-1/2 text-surface-400'>
                <Search strokeWidth={2} className='w-4 h-4' />
              </div>
              <input
                type='search'
                placeholder='Search by name or email...'
                className='input pl-11'
            onChange={(e) => {
              const { value } = e.target;
              const searchedUsers = users.filter((user) =>
                user.other_names.toLowerCase().includes(value.toLowerCase()) ||
                user.surname.toLowerCase().includes(value.toLowerCase()) ||
                (user.email && user.email.toLowerCase().includes(value.toLowerCase()))
              )
              setSystemUsers(searchedUsers);
            }}
          />
        </div>
      </div>
        </div>
      </div>

      {/* Users Table */}
      <div className='card p-0 overflow-hidden'>
        <table className="table-modern">
        <thead>
            <tr>
              <th className='w-12'></th>
              <th>User</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {systemUsers && systemUsers.length > 0 ?
              systemUsers.slice(startIndex, endIndex).map((user, index) => (
                <tr key={user.id || index}>
                  <td className='text-surface-400 font-medium'>
                    {startIndex + index + 1}
                  </td>
                  <td>
                    <div className="flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-xl object-cover ring-2 ring-surface-100"
                        src={user.profile_pic}
                        alt=""
                        loading="lazy"
                      />
                      <div>
                        <p className="font-semibold text-surface-800">
                          {user.surname} {user.other_names}
                        </p>
                        <p className="text-xs text-surface-400 capitalize">
                          {user.role}
                        </p>
                      </div>
                    </div>
                  </td>
                  <td>
                    {user.email === null || user.email === ''
                      ? <span className='text-surface-400 italic'>Not provided</span>
                      : user.email}
                  </td>
                  <td className='font-medium'>{user.phone_number}</td>
                  <td>
                    {user.user_account_setting?.is_active ? (
                      <span className='badge-success'>
                        <span className='w-1.5 h-1.5 rounded-full bg-success-500' />
                        Active
                      </span>
                    ) : (
                      <span className='badge-danger'>
                        <span className='w-1.5 h-1.5 rounded-full bg-danger-500' />
                        Blocked
                      </span>
                    )}
                  </td>
                  <td>
                    <div className='flex items-center gap-1'>
                      <button
                        onClick={() => navigator(`/users/view/${user.id}`)}
                        className='btn-ghost p-2 rounded-lg'
                        title='View Details'
                      >
                        <EyeEmpty strokeWidth={2} className='w-4 h-4' />
                      </button>
                      <button
                        onClick={() => navigator(`/users/edit/${user.id}`)}
                        className='btn-ghost p-2 rounded-lg'
                        title='Edit User'
                      >
                        <Edit strokeWidth={2} className='w-4 h-4' />
                      </button>
                    </div>
                  </td>
                </tr>
              )) :
              <tr>
                <td colSpan={6} className="text-center py-12">
                  <div className='flex flex-col items-center gap-3'>
                    <div className='w-16 h-16 rounded-2xl bg-surface-100 flex items-center justify-center'>
                      <Group strokeWidth={1.5} className='w-8 h-8 text-surface-400' />
                    </div>
                    <div>
                      <p className='font-medium text-surface-600'>No users found</p>
                      <p className='text-sm text-surface-400'>Try adjusting your search or filters</p>
                    </div>
                  </div>
                </td>
            </tr>
          }
        </tbody>
      </table>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className='border-t border-surface-100 px-6 py-4'>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default UsersPage
